import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Icon,
  IconButton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { FaUserFriends } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import FaceDetectionsTab from "./faces/FaceDetectionsTab";
import FaceManagementTab from "./faces/FaceManagementTab";

const Faces = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState(null);
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);
  const [isDraggingTabs, setIsDraggingTabs] = useState(false);
  const [tabsDragStartX, setTabsDragStartX] = useState(0);
  const [tabsScrollStartLeft, setTabsScrollStartLeft] = useState(0);

  const tabsCarouselRef = useRef(null);
  const pageBg = useColorModeValue("gray.100", "gray.800");

  const tabsItems = [
    { label: "Detecções" },
    { label: "Gestão de Rostos" }
  ];

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      setUserLevel(decoded.role);
      setUserId(decoded.userId);
    }
  }, []);

  const updateTabsScrollState = useCallback(() => {
    const node = tabsCarouselRef.current;
    if (!node) return;
    const maxLeft = node.scrollWidth - node.clientWidth;
    setCanScrollTabsLeft(node.scrollLeft > 2);
    setCanScrollTabsRight(maxLeft - node.scrollLeft > 2);
  }, []);

  const getTabsStepWidth = useCallback(() => {
    const node = tabsCarouselRef.current;
    if (!node) return 0;
    const firstTab = node.querySelector('[role="tab"]');
    const tabList = node.querySelector('[role="tablist"]');
    if (!firstTab) return node.clientWidth / 2;
    const tabWidth = firstTab.getBoundingClientRect().width;
    const gapValue = tabList ? window.getComputedStyle(tabList).columnGap || window.getComputedStyle(tabList).gap : "0";
    const gap = Number.parseFloat(gapValue) || 0;
    return tabWidth + gap;
  }, []);

  const scrollTabsCarousel = useCallback((direction) => {
    const node = tabsCarouselRef.current;
    if (!node) return;
    node.scrollBy({ left: direction * getTabsStepWidth(), behavior: "smooth" });
  }, [getTabsStepWidth]);

  const handleTabsPointerDown = (event) => {
    const node = tabsCarouselRef.current;
    if (!node) return;
    setIsDraggingTabs(true);
    setTabsDragStartX(event.clientX);
    setTabsScrollStartLeft(node.scrollLeft);
  };

  const handleTabsPointerMove = (event) => {
    if (!isDraggingTabs) return;
    const node = tabsCarouselRef.current;
    if (!node) return;
    node.scrollLeft = tabsScrollStartLeft - (event.clientX - tabsDragStartX);
    updateTabsScrollState();
  };

  const handleTabsPointerUp = () => {
    setIsDraggingTabs(false);
  };

  useEffect(() => {
    updateTabsScrollState();
    window.addEventListener("resize", updateTabsScrollState);
    return () => window.removeEventListener("resize", updateTabsScrollState);
  }, [updateTabsScrollState]);

  return (
    <Box p={{ base: 3, md: 6 }} flex="1" minH="100vh" bg={pageBg} w="100%" maxW="100%" overflowX="hidden">
      <Flex
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={3}
        mb={4}
      >
        <Box>
          <Heading display="flex" alignItems="center" fontSize={{ base: "xl", md: "2xl" }} gap={3}>
            <Icon as={FaUserFriends} color="teal.500" />
            Reconhecimento Facial
          </Heading>
          <Text color="gray.500" mt={1} fontSize={{ base: "sm", md: "md" }}>
            Detecções em tempo real e gestão da galeria de rostos
          </Text>
        </Box>
      </Flex>

      <Tabs
        variant="soft-rounded"
        colorScheme="teal"
        mb={4}
        w="100%"
        maxW="100%"
        minW={0}
        index={activeTab}
        onChange={setActiveTab}
      >
        <Box position="relative" w="100%" maxW="100%" minW={0}>
          <Box
            ref={tabsCarouselRef}
            overflowX="auto"
            pb={1}
            minW={0}
            mx={{ base: 9, md: 0 }}
            onScroll={updateTabsScrollState}
            onPointerDown={handleTabsPointerDown}
            onPointerMove={handleTabsPointerMove}
            onPointerUp={handleTabsPointerUp}
            onPointerLeave={handleTabsPointerUp}
            cursor={{ base: isDraggingTabs ? "grabbing" : "grab", md: "default" }}
            touchAction="pan-x"
            sx={{
              scrollSnapType: { base: "x mandatory", md: "none" },
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none"
            }}
          >
            <TabList minW={{ base: "100%", md: "max-content" }} flexWrap="nowrap" gap={{ base: 2, md: 0 }}>
              {tabsItems.map((tabItem) => (
                <Tab
                  key={tabItem.label}
                  flex={{ base: "0 0 calc((100% - 0.5rem) / 2)", md: "0 0 auto" }}
                  scrollSnapAlign={{ base: "center", md: "none" }}
                  fontSize={{ base: "xs", md: "sm" }}
                  px={{ base: 2, md: 4 }}
                  py={{ base: 2, md: 2 }}
                  minH={{ base: "56px", md: "auto" }}
                  whiteSpace="normal"
                  textAlign="center"
                  lineHeight="1.2"
                  borderRadius={{ base: "xl", md: "md" }}
                  borderWidth={{ base: "1px", md: "0" }}
                  borderColor={{ base: "gray.200", md: "transparent" }}
                  bg={{ base: "white", md: "transparent" }}
                  _selected={{
                    bg: { base: "teal.500", md: "teal.100" },
                    color: { base: "white", md: "teal.700" },
                    borderColor: { base: "teal.500", md: "transparent" },
                    boxShadow: { base: "sm", md: "none" }
                  }}
                >
                  {tabItem.label}
                </Tab>
              ))}
            </TabList>
          </Box>

          <IconButton
            aria-label="Rolar abas para a esquerda"
            icon={<ChevronLeftIcon boxSize={5} />}
            size="sm"
            position="absolute"
            left="0"
            top="50%"
            transform="translateY(-50%)"
            display={{ base: "inline-flex", md: "none" }}
            borderRadius="full"
            boxShadow="md"
            bg="white"
            onClick={() => scrollTabsCarousel(-1)}
            isDisabled={!canScrollTabsLeft}
            zIndex={2}
          />
          <IconButton
            aria-label="Rolar abas para a direita"
            icon={<ChevronRightIcon boxSize={5} />}
            size="sm"
            position="absolute"
            right="0"
            top="50%"
            transform="translateY(-50%)"
            display={{ base: "inline-flex", md: "none" }}
            borderRadius="full"
            boxShadow="md"
            bg="white"
            onClick={() => scrollTabsCarousel(1)}
            isDisabled={!canScrollTabsRight}
            zIndex={2}
          />
        </Box>

        <TabPanels w="100%" maxW="100%" minW={0}>
          <TabPanel px={{ base: 0, md: 4 }}>
            <FaceDetectionsTab />
          </TabPanel>
          <TabPanel px={{ base: 0, md: 4 }}>
            <FaceManagementTab userLevel={userLevel} userId={userId} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default Faces;
